import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import {
  Printer,
  MessageCircle,
  Mail,
  Home,
  CheckCircle2,
  Clock,
  AlertTriangle,
  RefreshCw,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import logoAsset from "@/assets/logo.jpg.asset.json";
import { getBookingForInvoice } from "@/lib/api/invoice.functions";
import { services } from "@/lib/services-data";
import { site } from "@/lib/site";
import { toast } from "sonner";
import { PayFastCheckoutButton } from "@/components/site/PayFastCheckoutButton";

export const Route = createFileRoute("/invoice/$id")({
  head: () => ({
    meta: [
      { title: "Your Consultation Invoice | Nkadimeng Danny Legal" },
      { name: "robots", content: "noindex" },
    ],
  }),
  errorComponent: () => (
    <div className="container-narrow py-24 text-center">
      <p className="text-muted-foreground">
        We couldn't load that invoice. Please check the link or{" "}
        <Link to="/book" className="underline">book again</Link>.
      </p>
    </div>
  ),
  notFoundComponent: () => (
    <div className="container-narrow py-24 text-center">Invoice not found.</div>
  ),
  component: InvoicePage,
});

function formatZar(n: number) {
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
  }).format(n);
}

function invoiceNumber(id: string, createdAt: string) {
  const d = new Date(createdAt);
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `INV-${yy}${mm}-${id.slice(0, 6).toUpperCase()}`;
}

function InvoicePage() {
  const { id } = Route.useParams();
  const fetchInvoice = useServerFn(getBookingForInvoice);
  const { data, isLoading, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ["invoice", id],
    queryFn: () => fetchInvoice({ data: { id } }),
    // Poll for live payment status updates while page is open.
    // Stop polling once payment is confirmed or definitively failed.
    refetchInterval: (q) => {
      const s = (q.state.data as any)?.payment_status;
      if (s === "paid" || s === "refunded") return false;
      return 8000;
    },
    refetchOnWindowFocus: true,
  });

  const [lastNotified, setLastNotified] = useState<string | null>(null);
  useEffect(() => {
    if (!data) return;
    const s = (data as any).payment_status as string | null;
    if (!s || s === lastNotified) return;
    if (s === "paid") toast.success("Payment confirmed — your slot is booked.");
    else if (s === "failed") toast.error("Payment failed. You can retry below.");
    else if (s === "pending") toast("Payment received — awaiting confirmation.");
    setLastNotified(s);
  }, [data, lastNotified]);

  if (isLoading || !data) {
    return (
      <div className="container-narrow py-24 text-center text-muted-foreground">
        Generating your invoice…
      </div>
    );
  }


  const svc = services.find((s) => s.slug === data.service);
  const serviceTitle = svc?.title ?? data.service;
  const fee = site.consultationFee;
  const inv = invoiceNumber(data.id, data.created_at);

  const whatsappText = encodeURIComponent(
    `Hi ${data.full_name.split(" ")[0]}, here is your consultation invoice from ${site.shortName}.\n\n` +
      `Invoice: ${inv}\n` +
      `Service: ${serviceTitle}\n` +
      `When: ${data.preferred_date} at ${data.preferred_time} (${data.consultation_type})\n` +
      `Amount: ${formatZar(fee)}\n\n` +
      `Banking:\n${site.banking.accountName}\n${site.banking.bank} · Acc ${site.banking.accountNumber} · Branch ${site.banking.branchCode}\nReference: ${inv}\n\n` +
      `View / print: ${typeof window !== "undefined" ? window.location.href : ""}`,
  );
  const clientWa = data.phone.replace(/\D/g, "").replace(/^0/, "27");
  const waClientLink = `https://wa.me/${clientWa}?text=${whatsappText}`;
  const waAdminLink = `https://wa.me/${site.whatsapp}?text=${whatsappText}`;

  const mailtoSubject = encodeURIComponent(
    `Your consultation invoice ${inv} — ${site.shortName}`,
  );
  const mailtoBody = encodeURIComponent(
    `Hi ${data.full_name.split(" ")[0]},\n\n` +
      `Thank you for booking a consultation with ${site.name}.\n\n` +
      `INVOICE ${inv}\n` +
      `Service: ${serviceTitle}\n` +
      `Date: ${data.preferred_date} at ${data.preferred_time}\n` +
      `Format: ${data.consultation_type}\n` +
      `Amount due: ${formatZar(fee)}\n\n` +
      `BANKING DETAILS\n` +
      `${site.banking.accountName}\n` +
      `${site.banking.bank}\n` +
      `Account: ${site.banking.accountNumber}\n` +
      `Branch code: ${site.banking.branchCode}\n` +
      `Reference: ${inv}\n\n` +
      `Once paid, please reply with proof of payment. We will confirm your slot by SMS.\n\n` +
      `Kind regards,\n${site.shortName}\n${site.phone} · ${site.email}`,
  );
  const mailtoLink = `mailto:${data.email}?subject=${mailtoSubject}&body=${mailtoBody}`;

  return (
    <section className="bg-secondary/30 py-10 print:bg-white print:py-0">
      <div className="container-narrow">
        {/* Live payment status banner — hidden on print */}
        <PaymentStatusBanner
          status={(data as any).payment_status ?? "unpaid"}
          updatedAt={(data as any).payment_updated_at ?? data.created_at}
          lastChecked={dataUpdatedAt}
          isFetching={isFetching}
          onRefresh={() => refetch()}
          mailtoLink={mailtoLink}
          waClientLink={waClientLink}
          retryHref={`/book?service=${encodeURIComponent(data.service)}`}
        />

        {/* Detailed failure diagnostics — only when payment failed */}
        {((data as any).payment_status === "failed" ||
          (data as any).payment_status === "refunded") && (
          <PaymentDiagnostics
            event={(data as any).last_payment_event}
            invoiceRef={inv}
            bookingId={data.id}
            customerName={data.full_name}
            retryHref={`/book?service=${encodeURIComponent(data.service)}`}
          />
        )}




        {/* Action toolbar */}
        <div className="mb-4 flex flex-wrap gap-2 print:hidden">
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-navy-foreground hover:bg-navy/90"
          >
            <Printer className="h-4 w-4" /> Print / Save PDF
          </button>
          <a
            href={waClientLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md bg-[#25D366] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            <MessageCircle className="h-4 w-4" /> Send to my WhatsApp
          </a>
          <a
            href={mailtoLink}
            className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary"
          >
            <Mail className="h-4 w-4" /> Email me a copy
          </a>
          <a
            href={waAdminLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp the office
          </a>
        </div>

        {/* Invoice document */}
        <article className="rounded-2xl border border-border bg-white p-8 shadow-lifted print:rounded-none print:border-0 print:shadow-none md:p-12">
          <header className="flex flex-wrap items-start justify-between gap-6 border-b border-border pb-6">
            <div className="flex items-start gap-4">
              <img
                src={logoAsset.url}
                alt={`${site.shortName} logo`}
                width={64}
                height={64}
                className="h-16 w-16 rounded-md border border-border object-cover"
              />
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-gold">
                  Service Receipt
                </p>
                <h1 className="mt-1 font-display text-2xl text-navy">
                  {site.name}
                </h1>
                <p className="mt-1 text-xs text-muted-foreground">
                  {site.email} · {site.phone}
                </p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  Reg {site.registration.number}
                  {site.registration.taxNumber
                    ? ` · Tax ${site.registration.taxNumber}`
                    : ""}
                  {site.registration.vatNumber
                    ? ` · VAT ${site.registration.vatNumber}`
                    : ""}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {site.address.line1}, {site.address.line2},{" "}
                  {site.address.postalCode}
                </p>
              </div>
            </div>
            <div className="text-right text-sm">
              <p className="font-semibold text-navy">{inv}</p>
              <p className="text-muted-foreground">
                Issued: {new Date(data.created_at).toLocaleDateString("en-ZA")}
              </p>
              <p className="text-muted-foreground">
                Due: on receipt
              </p>
            </div>
          </header>

          <section className="mt-6 grid gap-6 sm:grid-cols-2">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                Billed to
              </p>
              <p className="mt-1 font-medium text-navy">{data.full_name}</p>
              <p className="text-sm text-muted-foreground">{data.email}</p>
              <p className="text-sm text-muted-foreground">{data.phone}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                Consultation
              </p>
              <p className="mt-1 font-medium text-navy">
                {data.preferred_date} · {data.preferred_time}
              </p>
              <p className="text-sm capitalize text-muted-foreground">
                Format: {data.consultation_type}
              </p>
            </div>
          </section>

          <section className="mt-8 overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Description</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-border">
                  <td className="px-4 py-4">
                    <p className="font-medium text-navy">{serviceTitle}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Initial consultation & matter scoping
                    </p>
                  </td>
                  <td className="px-4 py-4 text-right text-navy">
                    {formatZar(fee)}
                  </td>
                </tr>
                <tr className="border-t border-border bg-secondary/50">
                  <td className="px-4 py-3 text-right font-semibold text-navy">
                    Total due
                  </td>
                  <td className="px-4 py-3 text-right font-display text-lg text-navy">
                    {formatZar(fee)}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          {(data as any).payment_status !== "paid" && (
            <section className="mt-8 rounded-2xl border border-border bg-card p-6 shadow-soft">
              <h3 className="font-display text-lg text-navy">
                Pay securely with PayFast
              </h3>
              <p className="mt-2 text-xs text-muted-foreground">
                Pay {formatZar(fee)} by card, Instant EFT, SnapScan, Zapper or
                PayFast wallet. We never see your card details. Your order is
                released automatically the moment PayFast confirms payment.
              </p>
              <div className="mt-4">
                <PayFastCheckoutButton
                  reference={inv}
                  label={`Pay ${formatZar(fee)} with PayFast`}
                />
              </div>
            </section>
          )}



          <section className="mt-8 grid gap-6 sm:grid-cols-2">
            <div className="rounded-lg border border-border bg-secondary/30 p-4">
              <p className="text-xs uppercase tracking-wider text-gold">
                Banking details
              </p>
              <dl className="mt-2 space-y-1 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Account name</dt>
                  <dd className="text-navy">{site.banking.accountName}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Bank</dt>
                  <dd className="text-navy">{site.banking.bank}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Account no.</dt>
                  <dd className="text-navy">{site.banking.accountNumber}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Branch code</dt>
                  <dd className="text-navy">{site.banking.branchCode}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Reference</dt>
                  <dd className="font-semibold text-navy">{inv}</dd>
                </div>
              </dl>
            </div>
            <div className="rounded-lg border border-border p-4 text-xs leading-relaxed text-muted-foreground">
              <p>
                <strong className="text-navy">Payment terms:</strong> Payable on
                receipt. Your consultation slot is provisionally held and is
                confirmed by email as soon as proof of payment is received.
              </p>
              <p className="mt-2">
                <strong className="text-navy">Scope:</strong> This receipt
                covers the initial consultation and matter scoping. A separate
                quotation will be issued for any further work after we have
                scoped your matter.
              </p>
              <p className="mt-2">
                Nkadimeng Danny is an LLB graduate (UNISA) and is not an
                admitted attorney. We provide legal support, paralegal and
                compliance services only — this document is a service
                receipt, not a SARS tax invoice (not a VAT-registered vendor).
                POPIA-aligned handling.
              </p>
            </div>
          </section>

          {data.message ? (
            <section className="mt-6 rounded-lg border border-dashed border-border p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                Your note
              </p>
              <p className="mt-1 text-sm text-navy">{data.message}</p>
            </section>
          ) : null}
        </article>

        <div className="mt-6 flex flex-wrap gap-3 print:hidden">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary"
          >
            <Home className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </div>
    </section>
  );
}

type StatusKey = "unpaid" | "pending" | "paid" | "failed" | "refunded" | string;

const STATUS_META: Record<
  string,
  { label: string; tone: string; icon: any; title: string; desc: string }
> = {
  unpaid: {
    label: "Awaiting payment",
    tone: "border-gold/40 bg-card",
    icon: Clock,
    title: "Booking received — awaiting your payment.",
    desc: "Use the banking details below to make payment, then reply with proof. We update this page automatically as soon as payment is confirmed.",
  },
  pending: {
    label: "Payment received — verifying",
    tone: "border-amber-300 bg-amber-50",
    icon: Loader2,
    title: "Payment received — verifying with the bank.",
    desc: "This usually takes a few minutes. You can keep this page open — it refreshes automatically.",
  },
  paid: {
    label: "Paid — slot confirmed",
    tone: "border-emerald-300 bg-emerald-50",
    icon: CheckCircle2,
    title: "Payment confirmed. Your slot is booked.",
    desc: "A receipt has been sent to your email. We'll email a reminder before your consultation.",
  },
  failed: {
    label: "Payment failed",
    tone: "border-red-300 bg-red-50",
    icon: AlertTriangle,
    title: "Payment could not be confirmed.",
    desc: "Your slot is still held briefly. Retry payment using the banking details below, or rebook a new time.",
  },
  refunded: {
    label: "Refunded",
    tone: "border-slate-300 bg-slate-50",
    icon: ShieldCheck,
    title: "This booking has been refunded.",
    desc: "If this is unexpected, please contact our office.",
  },
};

function PaymentStatusBanner({
  status,
  updatedAt,
  lastChecked,
  isFetching,
  onRefresh,
  mailtoLink,
  waClientLink,
  retryHref,
}: {
  status: StatusKey;
  updatedAt: string;
  lastChecked: number;
  isFetching: boolean;
  onRefresh: () => void;
  mailtoLink: string;
  waClientLink: string;
  retryHref: string;
}) {
  const meta = STATUS_META[status] ?? STATUS_META.unpaid;
  const Icon = meta.icon;
  const spin = status === "pending";
  const showRetry = status === "failed" || status === "unpaid";
  const updated = new Date(updatedAt);
  const checked = new Date(lastChecked);

  return (
    <div
      className={`mb-6 rounded-xl border p-4 shadow-soft print:hidden ${meta.tone}`}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <Icon
          className={`mt-0.5 h-5 w-5 text-navy ${spin ? "animate-spin" : ""}`}
        />
        <div className="flex-1 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-semibold text-navy">{meta.title}</p>
            <span className="rounded-full border border-border bg-background px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider text-navy">
              {meta.label}
            </span>
          </div>
          <p className="mt-1 text-muted-foreground">{meta.desc}</p>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Status updated {updated.toLocaleString("en-ZA")} · Last checked{" "}
            {checked.toLocaleTimeString("en-ZA")}
          </p>

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onRefresh}
              disabled={isFetching}
              className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-secondary disabled:opacity-60"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`}
              />
              {isFetching ? "Checking…" : "Check now"}
            </button>
            <a
              href={mailtoLink}
              className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-secondary"
            >
              <Mail className="h-3.5 w-3.5" /> Resend receipt
            </a>
            <a
              href={waClientLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-secondary"
            >
              <MessageCircle className="h-3.5 w-3.5" /> Send to WhatsApp
            </a>
            {showRetry ? (
              <a
                href={retryHref}
                className="inline-flex items-center gap-1.5 rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-navy-foreground hover:bg-navy/90"
              >
                Retry / rebook
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Payment failure diagnostics
// -----------------------------------------------------------------------------

type DiagnosisCode =
  | "insufficient_funds"
  | "card_declined"
  | "expired_card"
  | "incorrect_cvc"
  | "processing_error"
  | "do_not_honor"
  | "limit_exceeded"
  | "duplicate_transaction"
  | "reference_mismatch"
  | "bank_timeout"
  | "refunded"
  | "unknown";

const DIAGNOSIS_LIBRARY: Record<
  DiagnosisCode,
  { title: string; summary: string; fixes: string[] }
> = {
  insufficient_funds: {
    title: "Insufficient funds",
    summary:
      "Your bank reported that the account does not have enough available balance to cover this payment.",
    fixes: [
      "Check your available (not just current) balance.",
      "Transfer funds from another account, then retry the EFT.",
      "Pay in two smaller transfers using the same reference.",
    ],
  },
  card_declined: {
    title: "Payment declined by your bank",
    summary:
      "Your bank declined the transaction without giving us a specific reason.",
    fixes: [
      "Call your bank's fraud line to approve the payment.",
      "Try again from your banking app rather than a browser.",
      "Use a different account or payment method.",
    ],
  },
  expired_card: {
    title: "Card or mandate expired",
    summary: "The payment instrument used has expired or is no longer valid.",
    fixes: [
      "Use a current card or active bank account.",
      "Update your payment details with your bank.",
    ],
  },
  incorrect_cvc: {
    title: "Card details rejected",
    summary: "The card number, CVC, or expiry entered did not match the card.",
    fixes: [
      "Re-enter the card details carefully.",
      "Confirm the billing address matches what your bank holds on file.",
    ],
  },
  processing_error: {
    title: "Processing error",
    summary:
      "The payment provider returned a temporary processing error before the bank could respond.",
    fixes: [
      "Wait 2–3 minutes and try again.",
      "If it repeats, try a different network (mobile data vs Wi-Fi).",
    ],
  },
  do_not_honor: {
    title: "Bank refused the transaction (do not honour)",
    summary:
      "Your bank told us not to honour the payment — usually a fraud, daily-limit, or risk-rule trigger on their side.",
    fixes: [
      "Contact your bank to authorise the payment to us.",
      "Confirm online / merchant payments are enabled on the account.",
    ],
  },
  limit_exceeded: {
    title: "Daily or transaction limit exceeded",
    summary: "The amount exceeds a limit configured on your account or card.",
    fixes: [
      "Temporarily raise the limit in your banking app.",
      "Split the payment across two days using the same reference.",
    ],
  },
  duplicate_transaction: {
    title: "Duplicate transaction detected",
    summary:
      "The bank flagged this as an exact duplicate of a recent payment and blocked it.",
    fixes: [
      "Check your bank statement — the earlier payment may already have gone through.",
      "If it has not, wait 10 minutes and retry.",
    ],
  },
  reference_mismatch: {
    title: "Reference mismatch",
    summary:
      "Payment was received but the reference does not match this invoice, so we cannot auto-match it.",
    fixes: [
      "Reply with proof of payment so we can match it manually — use the reference shown below.",
      "On future EFTs, copy the reference exactly as shown on the invoice.",
    ],
  },
  bank_timeout: {
    title: "Bank timeout",
    summary:
      "We did not receive a confirmation from the bank within the expected time.",
    fixes: [
      "Check your bank statement to see whether the payment cleared.",
      "If it did not, retry the payment.",
    ],
  },
  refunded: {
    title: "Payment refunded",
    summary: "This payment has been reversed and the funds returned.",
    fixes: [
      "If the refund was unexpected, contact our office for clarification.",
      "To rebook, use the Retry / rebook button.",
    ],
  },
  unknown: {
    title: "Payment could not be confirmed",
    summary:
      "We don't yet have a specific reason from the payment provider — our team can investigate using the reference below.",
    fixes: [
      "Retry the payment, ideally from your banking app.",
      "If it still fails, send proof of payment or call our office.",
    ],
  },
};

function diagnose(event: any, status: string): DiagnosisCode {
  if (status === "refunded") return "refunded";
  const raw =
    `${event?.error ?? ""} ${event?.payload?.code ?? ""} ${event?.payload?.failure_code ?? ""} ${event?.payload?.message ?? ""} ${event?.payload?.decline_code ?? ""}`.toLowerCase();
  if (!raw.trim()) return "unknown";
  if (raw.includes("insufficient") || raw.includes("nsf")) return "insufficient_funds";
  if (raw.includes("expired")) return "expired_card";
  if (raw.includes("cvc") || raw.includes("cvv") || raw.includes("incorrect_number"))
    return "incorrect_cvc";
  if (raw.includes("do_not_honor") || raw.includes("do not honor")) return "do_not_honor";
  if (raw.includes("limit")) return "limit_exceeded";
  if (raw.includes("duplicate")) return "duplicate_transaction";
  if (raw.includes("reference")) return "reference_mismatch";
  if (raw.includes("timeout") || raw.includes("timed out")) return "bank_timeout";
  if (raw.includes("processing_error") || raw.includes("temporary")) return "processing_error";
  if (raw.includes("declined") || raw.includes("decline")) return "card_declined";
  return "unknown";
}

function PaymentDiagnostics({
  event,
  invoiceRef,
  bookingId,
  customerName,
  retryHref,
}: {
  event: any;
  invoiceRef: string;
  bookingId: string;
  customerName: string;
  retryHref: string;
}) {
  const status =
    event?.payload?.status === "refunded" ? "refunded" : "failed";
  const code = diagnose(event, status);
  const diag = DIAGNOSIS_LIBRARY[code];
  const provider = event?.provider ?? "manual";
  const providerCode =
    event?.payload?.code ??
    event?.payload?.failure_code ??
    event?.payload?.decline_code ??
    event?.event_type ??
    "N/A";
  const providerMessage = event?.error ?? event?.payload?.message ?? null;
  const occurredAt = event?.created_at
    ? new Date(event.created_at).toLocaleString("en-ZA")
    : "Not yet reported by provider";

  const supportSubject = encodeURIComponent(
    `Payment failure on ${invoiceRef} (${code})`,
  );
  const supportBody = encodeURIComponent(
    `Hi ${site.shortName} team,\n\n` +
      `I need help with a failed payment.\n\n` +
      `Name: ${customerName}\n` +
      `Invoice: ${invoiceRef}\n` +
      `Booking ID: ${bookingId}\n` +
      `Provider: ${provider}\n` +
      `Provider code: ${providerCode}\n` +
      `Provider message: ${providerMessage ?? "(none)"}\n` +
      `Detected reason: ${diag.title}\n` +
      `Time: ${occurredAt}\n\n` +
      `Please advise on next steps.\n`,
  );
  const supportMailto = `mailto:${site.email}?subject=${supportSubject}&body=${supportBody}`;
  const supportWa = `https://wa.me/${site.whatsapp}?text=${supportSubject}`;
  const supportTel = `tel:${(site as any).phoneIntl ?? site.phone}`;

  return (
    <div
      className="mb-6 rounded-xl border border-red-200 bg-white p-5 shadow-soft print:hidden"
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 text-red-600" />
        <div className="flex-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-red-600">
            Payment diagnostics
          </p>
          <h2 className="mt-1 font-display text-lg text-navy">{diag.title}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{diag.summary}</p>

          <dl className="mt-4 grid gap-3 rounded-lg border border-border bg-secondary/30 p-3 text-xs sm:grid-cols-2">
            <div>
              <dt className="uppercase tracking-wider text-muted-foreground">Provider</dt>
              <dd className="mt-0.5 font-medium text-navy">{provider}</dd>
            </div>
            <div>
              <dt className="uppercase tracking-wider text-muted-foreground">
                Provider error code
              </dt>
              <dd className="mt-0.5 font-mono text-navy">{providerCode}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="uppercase tracking-wider text-muted-foreground">
                Provider message
              </dt>
              <dd className="mt-0.5 break-words font-mono text-[11px] text-navy">
                {providerMessage ?? "Not provided by the payment provider."}
              </dd>
            </div>
            <div>
              <dt className="uppercase tracking-wider text-muted-foreground">Reported at</dt>
              <dd className="mt-0.5 text-navy">{occurredAt}</dd>
            </div>
            <div>
              <dt className="uppercase tracking-wider text-muted-foreground">Reference</dt>
              <dd className="mt-0.5 font-semibold text-navy">{invoiceRef}</dd>
            </div>
          </dl>

          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-navy">
              Suggested fixes
            </p>
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-foreground">
              {diag.fixes.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ol>
          </div>

          <div className="mt-5 rounded-lg border border-gold/40 bg-gold/5 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-gold">
              One-click support
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Your invoice, booking ID and the provider error are pre-filled for our team.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <a
                href={supportMailto}
                className="inline-flex items-center gap-1.5 rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-navy-foreground hover:bg-navy/90"
              >
                <Mail className="h-3.5 w-3.5" /> Email support
              </a>
              <a
                href={supportWa}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md bg-[#25D366] px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90"
              >
                <MessageCircle className="h-3.5 w-3.5" /> WhatsApp support
              </a>
              <a
                href={supportTel}
                className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-secondary"
              >
                Call {site.phone}
              </a>
              <a
                href={retryHref}
                className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-secondary"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Retry / rebook
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
